import { Link } from '@tanstack/react-router';
import { Star } from 'lucide-react';
import { Picture } from '@/components/media/Picture';
import { caseCover, caseAvatar } from '@/lib/media/case-images';
import bowlCover from '@/assets/case-bowlscoreboard-cover.png.asset.json';
import bowlAvatar from '@/assets/case-bowlscoreboard-avatar.jpg.asset.json';

const GRAD = '#5048E5';
const fgMuted = 'hsl(var(--muted-foreground))';
const surface = 'hsl(var(--card))';
const border = 'hsl(var(--border))';

export type CaseStudyId = 'eclipse' | 'bowlscoreboard';

export default function CaseStudyCard({ caseStudy = 'eclipse' }: { caseStudy?: CaseStudyId }) {
  const isBowl = caseStudy === 'bowlscoreboard';

  const hash = isBowl ? 'bowlscoreboard' : 'eclipse-paving';
  const badge = isBowl ? 'Case Study · BowlScoreboard' : 'Case Study · Driveways & Paving';
  const title = isBowl ? 'BowlScoreboard' : 'Eclipse Paving';
  const domain = isBowl ? 'bowlscoreboard.co.uk' : 'eclipsepaving.com';
  const person = isBowl
    ? { name: 'Chris P Spry', role: 'Founder & CEO' }
    : { name: 'Justin Kirby', role: 'Founder' };
  const excerpt = isBowl
    ? 'Brand, website and a new business model — clubs get a scoreboard free, funded by local sponsors. We now run their ad sales.'
    : 'From zero online presence to a fully managed digital operation — website, SEO & AEO, Google Ads & ongoing growth.';
  const stats = isBowl
    ? [
        { value: 'Free', label: 'Boards for clubs' },
        { value: '✓', label: 'Ongoing partner' },
        { value: '2', label: 'Services active' },
      ]
    : [
        { value: '£70k+', label: 'From ads' },
        { value: '✓', label: 'Ongoing client' },
        { value: '6', label: 'Services active' },
      ];
  const tags = isBowl
    ? ['Vision to Launch', 'Website', 'Brand', 'Ad Sales']
    : ['Web Design', 'SEO & AEO', 'Google Ads', 'GTM', 'Analytics', 'Google Business'];
  const ratingLabel = isBowl ? '5.0 · Ongoing partner' : '5.0 · Ongoing client';

  return (
    <Link
      to="/case-studies"
      hash={hash}
      className="group flex w-full max-w-sm flex-col overflow-hidden rounded-2xl transition-all hover:shadow-xl"
      style={{ background: surface, border: `1px solid ${border}`, boxShadow: '0 12px 40px hsl(243 75% 59% / 0.25)', textDecoration: 'none' }}
    >
      <div className="relative shrink-0 overflow-hidden" style={{ height: '180px' }}>
        {isBowl ? (
          <img
            src={bowlCover.url}
            alt="BowlScoreboard mechanical scoreboard — case study by Digitally Baffled LTD"
            width={384}
            height={180}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <Picture {...caseCover} alt="Eclipse Paving website — built by Digitally Baffled LTD" width={384} height={180} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        )}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, hsl(24 55% 10% / 0.85) 0%, transparent 55%)' }} aria-hidden="true" />
        <span className="absolute top-3 left-3 rounded-full px-2.5 py-1 font-bold uppercase tracking-widest" style={{ background: GRAD, color: '#ffffff', fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '0.55rem' }}>{badge}</span>
        <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
          <div>
            <p className="font-bold leading-none" style={{ color: '#ffffff', fontFamily: 'Outfit, sans-serif', fontSize: '1.2rem' }}>{title}</p>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '0.65rem' }}>{domain}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="rounded-full overflow-hidden shrink-0" style={{ width: '44px', height: '44px', minWidth: '44px', border: '2px solid #5048E5' }}>
              {isBowl ? (
                <img src={bowlAvatar.url} alt="Chris P Spry, founder & CEO of BowlScoreboard" width={44} height={44} loading="lazy" className="w-full h-full object-cover" />
              ) : (
                <Picture {...caseAvatar} alt="Justin Kirby, founder of Eclipse Paving" width={44} height={44} loading="lazy" className="w-full h-full object-cover" />
              )}
            </div>
            <div>
              <p className="font-bold leading-none" style={{ color: '#ffffff', fontFamily: 'Outfit, sans-serif', fontSize: '0.8rem' }}>{person.name}</p>
              <p style={{ color: 'rgba(255,255,255,0.65)', fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '0.65rem' }}>{person.role}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-3 p-4">
        <p className="text-xs leading-snug" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          {excerpt}
        </p>
        <div className="grid grid-cols-3 gap-2">
          {stats.map(s => (
            <div key={s.label} className="flex flex-col items-center rounded-xl py-2" style={{ background: 'hsla(243,75%,59%,0.08)', border: '1px solid hsla(243,75%,59%,0.15)' }}>
              <span className="font-bold leading-none" style={{ color: GRAD, fontFamily: 'Outfit, sans-serif', fontSize: '1rem' }}>{s.value}</span>
              <span className="text-center leading-tight mt-0.5" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '0.55rem' }}>{s.label}</span>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-1">
          {tags.map(tag => (
            <span key={tag} className="rounded-full px-2 py-0.5 font-semibold" style={{ background: 'hsla(243,75%,59%,0.1)', color: 'hsl(243 75% 65%)', fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '0.55rem' }}>{tag}</span>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => <Star key={i} size={10} fill="#f97316" style={{ color: '#f97316' }} />)}
            <span className="ml-1 text-xs" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{ratingLabel}</span>
          </div>
          <span className="font-bold text-xs" style={{ color: 'hsl(243 75% 65%)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>View case study →</span>
        </div>
      </div>
    </Link>
  );
}
